import { useCallback, useState } from "react";

import httpClient from "@/dataprovider/http-client";
import { getApiUrl } from "@/constants";
import { isTallyConfigReady, normalizeTallyConfig, type TallyConfig } from "@/types/tally-config";

type UseTallyConfigResult = {
  config: TallyConfig | null;
  ready: boolean;
  loading: boolean;
  error: string | null;
  fetchConfig: () => Promise<TallyConfig | null>;
};

export function useTallyConfig(): UseTallyConfigResult {
  const [config, setConfig] = useState<TallyConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async (): Promise<TallyConfig | null> => {
    setLoading(true);
    setError(null);
    try {
      const { json } = await httpClient(`${getApiUrl()}/tallyConfig`);
      const rows = (json as { rows?: { config?: unknown }[] })?.rows;
      const raw = rows?.[0]?.config;
      const normalized = normalizeTallyConfig(raw);
      setConfig(normalized);
      return normalized;
    } catch {
      setError("Could not load Tally config");
      setConfig(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    config,
    ready: isTallyConfigReady(config),
    loading,
    error,
    fetchConfig,
  };
}
